<template>
   <div :class="col">
      <div class="mb-0 position-relative sa-field">
         <!-- Label above the box (main.js formStyle 'icons' and 'plain') -->
         <label v-if="label && !floating" class="form-label">{{ label }}</label>

         <!-- $validateForm checks this hidden input (v-select has no real required box) -->
         <input ref="hidden" type="hidden" :name="name" :value="hiddenValue" :required="required" />

         <div :class="wrapClass" @focusin="focused = true" @focusout="focused = false">
            <!-- Icon in front (formStyle 'icons' only) -->
            <span v-if="showIcon" class="input-group-text"><i :class="icon"></i></span>

            <v-select
               v-bind="$attrs"
               :placeholder="floating ? '' : $attrs.placeholder"
               :model-value="modelValue"
               :label="optionLabel"
               class="custom-tooltip-input validation-field"
               :class="[stateClass, { 'flex-grow-1': showIcon }]"
               ref="select"
               @update:modelValue="onChange"
            >
               <!-- The page's own v-select slots (#option, #selected-option, #list-footer ...) are passed on -->
               <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
                  <slot :name="slotName" v-bind="slotProps || {}" />
               </template>
            </v-select>

            <!-- Floating label: inside the box, moves up onto the border (formStyle 'floating') -->
            <label v-if="floating" class="sa-float-label">{{ label }}</label>
         </div>

         <!-- Grey note under the box, e.g. "A branch cannot be moved to another company." -->
         <small v-if="hint" class="text-muted d-block sa-core-color">{{ hint }}</small>

         <!-- Above the box: on focus, and in red while the field is invalid -->
         <div v-if="tooltip" class="custom-tooltip">{{ tooltip }}<span class="tooltip-arrow"></span></div>
      </div>
   </div>
</template>

<script>
// A searchable dropdown (v-select) with the same looks as FormField (main.js formStyle).
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
   name: "SelectField",
   components: { vSelect },
   inheritAttrs: false, // options, reduce, placeholder, multiple, disabled, clearable... go to v-select
   props: {
      modelValue: { default: null },
      label: { type: String, default: "" },          // the field's label above the box
      optionLabel: { type: String, default: "label" }, // which key of each option to show
      name: { type: String, default: "" },
      icon: { type: String, default: "" },
      tooltip: { type: String, default: "" },
      hint: { type: String, default: "" },
      col: { type: String, default: "col-md-6" },
      required: { type: Boolean, default: false },
      submitted: { type: Boolean, default: false },  // pass the page's "submitted" (optional now)
   },
   emits: ["update:modelValue", "change"],
   data() {
      return {
         focused: false,
         checked: false, // true once $validateForm has checked this field (Save pressed)
      };
   },
   computed: {
      formStyle() {
         return this.$formStyle || "icons";
      },
      floating() {
         return this.formStyle === "floating" && !!this.label;
      },
      showIcon() {
         return this.formStyle === "icons" && !!this.icon;
      },
      // Same rule as $isValid: an array needs one item, anything else must not be empty
      hasValue() {
         const v = this.modelValue;
         if (Array.isArray(v)) return v.length > 0;
         return v !== null && v !== undefined && v !== "";
      },
      hiddenValue() {
         const v = this.modelValue;
         if (Array.isArray(v)) return v.length ? String(v.length) : "";
         return v ?? "";
      },
      // After Save: red when empty, green when filled (same as the other boxes).
      // Follows only $validateForm / $clearValidationStates (through "checked"), never the page's
      // "submitted", which stays true after a failed save and would get out of step.
      stateClass() {
         if (!this.required || !this.checked) return {};
         return {
            "is-valid": this.hasValue,
            "is-invalid": !this.hasValue,
         };
      },
      wrapClass() {
         if (this.showIcon) return "input-group";
         if (this.floating) {
            return { "sa-float": true, "is-up": this.focused || this.hasValue, "is-focused": this.focused };
         }
         return "";
      },
   },
   mounted() {
      // $validateForm marks the hidden input is-valid / is-invalid on Save, and
      // $clearValidationStates removes it: follow that, so the dropdown turns red too
      this.hiddenWatcher = new MutationObserver(() => {
         const classes = this.$refs.hidden?.classList;
         this.checked = !!classes && (classes.contains("is-invalid") || classes.contains("is-valid"));
      });
      if (this.$refs.hidden) {
         this.hiddenWatcher.observe(this.$refs.hidden, { attributes: true, attributeFilter: ["class"] });
      }
   },
   beforeUnmount() {
      this.hiddenWatcher?.disconnect();
   },
   methods: {
      onChange(value) {
         this.$emit("update:modelValue", value);
         this.$emit("change", value); // the page can call $clearValidationAlert here

         // Check the field straight away, like the text boxes do while you type:
         // green when something is picked, red when it is cleared again (required fields)
         this.$nextTick(() => {
            if (this.$refs.hidden && typeof this.$validateField === "function") {
               this.$validateField(this.$refs.hidden);
            }
         });
      },
   },
};
</script>